<script setup lang="ts">
import type { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import { useBrowserStore } from '@apps/main/new-desktop/core/stores';

import { TransportType } from '@libs/trip-planner-interfaces';

import type { AvailableItineraryFlightSegment } from '../../../models';
import ChangeProductLink from '../ChangeProductLink.vue';
import ItineraryTimeline from '../ItineraryTimeline.vue';
import ItineraryTransportSelector from '../ItineraryTransportSelector.vue';
import { useTripOverviewPageStore } from '../../../stores';

import FlightProductCard from './FlightProductCard.vue';
import FlightSegmentHeader from './FlightSegmentHeader.vue';

interface ItineraryFlightSegmentProps {
  segment: AvailableItineraryFlightSegment;
  printMode: boolean;
}

const props = defineProps<ItineraryFlightSegmentProps>();

interface FlightProductCardEmits {
  (event: 'show-details'): void;
  (event: 'show-more-filters'): void;
  (event: 'suggestion-type-selected', suggestionType: SuggestionType): void;
  (event: 'select-transport', transportType: TransportType): void;
  (event: 'remove-filter', tagId: string): void;
}

const emit = defineEmits<FlightProductCardEmits>();

const tripOverviewPageStore = useTripOverviewPageStore();
const browserStore = useBrowserStore();
</script>

<template>
  <ItineraryTimeline :segment-type="props.segment.segmentType">
    <template #dropdown>
      <ItineraryTransportSelector
        :transport-type="TransportType.Flight"
        class="flight-selector"
        :disabled="props.printMode"
        @select-transport="
          tripOverviewPageStore.selectTransport(props.segment, $event)
        "
      />
    </template>
    <template v-if="!browserStore.isSmallerThan1200px" #header>
      <FlightSegmentHeader
        :segment="props.segment"
        :print-mode="props.printMode"
        @show-more-filters="emit('show-more-filters')"
        @suggestion-type-selected="emit('suggestion-type-selected', $event)"
        @remove-filter="emit('remove-filter', $event)"
      />
    </template>
    <template v-else #large-header>
      <FlightSegmentHeader
        :segment="props.segment"
        :print-mode="props.printMode"
        @show-more-filters="emit('show-more-filters')"
        @suggestion-type-selected="emit('suggestion-type-selected', $event)"
        @remove-filter="emit('remove-filter', $event)"
      />
    </template>

    <template #content>
      <div class="available-flight-segment-content-wrapper">
        <FlightProductCard
          :product-card-data="props.segment.productCardData"
          :print-mode="props.printMode"
          @show-details="emit('show-details')"
        />
        <ChangeProductLink
          v-if="!props.printMode"
          :link-context="{ segmentType: props.segment.segmentType }"
          class="change-product-link"
        />
      </div>
    </template>
  </ItineraryTimeline>
</template>

<style scoped lang="scss">
.available-flight-segment-content-wrapper {
  display: flex;
  flex-flow: column;
  gap: var(--spacing-16);

  .change-product-link {
    align-self: flex-end;
  }
}
</style>
